import React from "react";
import { Box } from "@mui/material";
import { EmptyChatWelcome } from "./EmptyChatWelcome";
import { ConversationStarters } from "./ConversationStarters";
import { CHAT_COLUMN_MAX_WIDTH, CHAT_COLUMN_GUTTER } from "./layout";

/**
 * The new-chat hero: greeting + composer + starters as one vertically centered
 * group. Shared by the SPA chat surface and the embed widget so an empty chat
 * looks the same in a full page and in a narrow embed panel (where the old
 * embed layout left the greeting stranded at the top and the composer pinned to
 * the bottom with dead space between them).
 *
 * `children` is the composer — the caller owns which one (the SPA's wires voice
 * mode, the embed's doesn't).
 */
export function ChatIntro({ children }: { children: React.ReactNode }) {
  return (
    <Box
      data-chat-intro="true"
      // inline-size container: everything inside sizes off the chat column's
      // own width, not the viewport's — the embed panel is narrow on a wide
      // page, so viewport breakpoints would resolve to their desktop values.
      className="@container"
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: CHAT_COLUMN_GUTTER,
        py: 4,
      }}
    >
      <Box
        // Optical centering: on a roomy column the group sits a little above
        // true center (as the SPA always has); in a narrow panel every pixel
        // of height counts, so the offset is dropped. Container-driven, so an
        // embed panel on a wide page gets the narrow treatment.
        className="@md:mb-16"
        sx={{
          width: "100%",
          maxWidth: CHAT_COLUMN_MAX_WIDTH,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <EmptyChatWelcome />
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 1 }}>
          {children}
        </Box>
        <ConversationStarters />
      </Box>
    </Box>
  );
}
